import React from "react";

import _ from "lodash";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";

import { ButtonBase, Snackbar, SnackbarOrigin } from "@mui/material";
import Slide, { SlideProps } from "@mui/material/Slide";

type SnackbarType = {
    key: string;
    isOpen: boolean;
    isVisible: boolean;
    visibleIndex: number;
    variant: "SUCCESS" | "ERROR";
    message: string | JSX.Element;
};

export const SnackbarActionsContext = React.createContext({
    enqueueSnackbar: (message: string | JSX.Element, kind: "SUCCESS" | "ERROR") => {
        // empty function
    },
});

export const useSnackbarActionsContext = () => React.useContext(SnackbarActionsContext);

function SlideTransition(props: SlideProps) {
    return <Slide {...props} direction={"left"} />;
}

function SnackbarItem({
    snackbar,
    autoHideDuration,
    anchorOrigin,
    extraTop,
    setSnackbars,
}: {
    snackbar: SnackbarType;
    autoHideDuration: number;
    anchorOrigin: SnackbarOrigin;
    extraTop: number;
    setSnackbars: React.Dispatch<React.SetStateAction<SnackbarType[]>>;
}) {
    const handleCloseSnackbar = React.useCallback(
        (event?: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === "clickaway") {
                return;
            }

            setSnackbars((prevSnackbars) => {
                const newSnackbars = _.cloneDeep(prevSnackbars);

                newSnackbars.forEach((item) => {
                    if (item.key === snackbar.key) {
                        item.isOpen = false;
                    }
                });

                return newSnackbars;
            });

            setTimeout(
                () =>
                    setSnackbars((prevSnackbars) => {
                        const newSnackbars = _.cloneDeep(prevSnackbars);

                        newSnackbars.forEach((item) => {
                            if (item.key === snackbar.key) {
                                item.isVisible = false;
                            }
                        });

                        let visibleIndex = 0;

                        newSnackbars.forEach((item) => {
                            item.visibleIndex = visibleIndex;

                            if (item.isVisible) {
                                visibleIndex += 1;
                            }
                        });

                        return newSnackbars;
                    }),
                300
            );
        },
        [setSnackbars, snackbar]
    );

    const bottom = React.useMemo(
        (): string | undefined =>
            anchorOrigin.vertical === "bottom" ? snackbar.visibleIndex * 70 + 24 + "px" : undefined,
        [snackbar, anchorOrigin.vertical]
    );

    const top = React.useMemo(
        (): string | undefined =>
            anchorOrigin.vertical === "top"
                ? snackbar.visibleIndex * 60 + 24 + extraTop + "px"
                : undefined,
        [snackbar, anchorOrigin.vertical, extraTop]
    );

    return (
        <Snackbar
            open={snackbar.isOpen}
            anchorOrigin={anchorOrigin}
            autoHideDuration={autoHideDuration}
            onClose={handleCloseSnackbar}
            TransitionComponent={SlideTransition}
            style={{ bottom, top }}
        >
            <div className={`alert-container ${snackbar.variant.toLowerCase()}`}>
                <div className={"icon"}>
                    {snackbar.variant === "SUCCESS" && <CheckCircleOutlineIcon color={"success"} />}

                    {snackbar.variant === "ERROR" && <ErrorIcon color={"error"} />}
                </div>
                <div className={`${snackbar.variant.toLowerCase()}-text`}>
                    {typeof snackbar.message === "string" ? (
                        <p>{snackbar.message}</p>
                    ) : (
                        snackbar.message
                    )}
                </div>
                <div className={"icon"} style={{ marginLeft: "0.5rem" }}>
                    <ButtonBase onClick={handleCloseSnackbar}>
                        <CloseIcon color={snackbar.variant === "SUCCESS" ? "success" : "error"} />
                    </ButtonBase>
                </div>
            </div>
        </Snackbar>
    );
}

type PropsType = {
    autoHideDuration?: number;
    anchorOrigin?: SnackbarOrigin;
    extraTop?: number;
    children: React.ReactNode;
};

export const SnackbarContextProvider = ({
    autoHideDuration = 6000,
    anchorOrigin = { vertical: "bottom", horizontal: "right" },
    extraTop = 0,
    children,
}: PropsType) => {
    const [snackbars, setSnackbars] = React.useState<Array<SnackbarType>>([]);

    const enqueueSnackbar = React.useCallback(
        (message: string | JSX.Element, variant: "SUCCESS" | "ERROR") =>
            setSnackbars((prevSnackbars) => {
                const newSnackbars = _.cloneDeep(prevSnackbars);

                const visibleLength = prevSnackbars.reduce((prevValue, snackbar) => {
                    return prevValue + (snackbar.isVisible ? 1 : 0);
                }, 0);

                newSnackbars.push({
                    key: "snackbar-" + prevSnackbars.length,
                    visibleIndex: visibleLength,
                    isOpen: true,
                    isVisible: true,
                    variant,
                    message,
                });

                return newSnackbars;
            }),
        []
    );

    const snackbarActions = React.useMemo(() => ({ enqueueSnackbar }), [enqueueSnackbar]);

    return (
        <SnackbarActionsContext.Provider value={snackbarActions}>
            {children}

            {snackbars.map((snackbar) =>
                snackbar.isVisible ? (
                    <SnackbarItem
                        key={snackbar.key}
                        snackbar={snackbar}
                        autoHideDuration={autoHideDuration}
                        anchorOrigin={anchorOrigin}
                        extraTop={extraTop}
                        setSnackbars={setSnackbars}
                    />
                ) : null
            )}
        </SnackbarActionsContext.Provider>
    );
};
